/**
 * Watch Purchase Form — Uhrinstinkt
 * Datei: assets/watch-purchase.js
 *
 * Verantwortlich für:
 * - Drag & Drop und Datei-Auswahl
 * - HEIC-Erkennung und Preview
 * - Validierung und Submit-Handling
 * - AWS-Presigned-URL-Upload (direkt zu S3)
 * - Success-Overlay und Fehler-Toasts
 */

(function () {
  'use strict';

  // ---------------------------------------------------------
  // CONFIGURATION
  // ---------------------------------------------------------

  const CONFIG = {
    API_URL: 'https://onfsjc5l00.execute-api.eu-central-1.amazonaws.com/ankauf',
    MAX_FILE_SIZE: 10 * 1024 * 1024, // 10 MB
    TOAST_DURATION: 5000
  };

  // ---------------------------------------------------------
  // BOOT
  // ---------------------------------------------------------

  document.addEventListener('DOMContentLoaded', function () {
    const form = document.getElementById('watchPurchaseForm');
    if (!form) return;

    new WatchPurchaseForm(form);
  });

  // ---------------------------------------------------------
  // FORM CLASS
  // ---------------------------------------------------------

  function WatchPurchaseForm(form) {
    this.form = form;
    this.selectedFiles = [];

    // DOM-Referenzen
    this.uploadArea = document.getElementById('uploadArea');
    this.fileInput = document.getElementById('fileInput');
    this.uploadButton = document.getElementById('uploadButton');
    this.previewGrid = document.getElementById('previewGrid');
    this.submitButton = document.getElementById('submitButton');
    this.submitLabel = this.submitButton && this.submitButton.querySelector('.wpf__submit-label');
    this.defaultSubmitText = this.submitLabel ? this.submitLabel.textContent : 'Anfrage absenden';

    // Erfolgs-Overlay (gemeinsames Form-Kit) und Toast
    this.overlayId = 'watchPurchaseSuccess';
    this.toast = document.getElementById('wpfToast');
    this.toastMessage = document.getElementById('wpfToastMessage');
    this.toastTimer = null;

    this.init();
  }

  WatchPurchaseForm.prototype.init = function () {
    this.bindUpload();
    this.bindDragDrop();
    this.bindPreviewRemove();
    this.bindSubmit();
  };

  // ---------------------------------------------------------
  // FILE UPLOAD — Button + Input
  // ---------------------------------------------------------

  WatchPurchaseForm.prototype.bindUpload = function () {
    const self = this;

    if (!this.uploadArea || !this.fileInput) return;

    // Button-Klick öffnet Datei-Dialog
    if (this.uploadButton) {
      this.uploadButton.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.fileInput.click();
      });
    }

    // Klick aufs Upload-Feld (nicht auf Button) öffnet ebenfalls
    this.uploadArea.addEventListener('click', function (e) {
      if (e.target === self.uploadButton || self.uploadButton.contains(e.target)) return;
      self.fileInput.click();
    });

    // Dateien ausgewählt
    this.fileInput.addEventListener('change', function (e) {
      self.handleFiles(Array.from(e.target.files));
      // Input zurücksetzen, damit dieselbe Datei erneut auswählbar ist
      self.fileInput.value = '';
    });
  };

  // ---------------------------------------------------------
  // DRAG & DROP
  // ---------------------------------------------------------

  WatchPurchaseForm.prototype.bindDragDrop = function () {
    const self = this;
    if (!this.uploadArea) return;

    ['dragenter', 'dragover'].forEach(function (event) {
      self.uploadArea.addEventListener(event, function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.uploadArea.classList.add('is-dragging');
      });
    });

    ['dragleave', 'drop'].forEach(function (event) {
      self.uploadArea.addEventListener(event, function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.uploadArea.classList.remove('is-dragging');
      });
    });

    this.uploadArea.addEventListener('drop', function (e) {
      const files = Array.from(e.dataTransfer.files).filter(function (file) {
        return file.type.startsWith('image/') || file.name.toLowerCase().endsWith('.heic');
      });
      self.handleFiles(files);
    });
  };

  // ---------------------------------------------------------
  // HANDLE FILES
  // ---------------------------------------------------------

  WatchPurchaseForm.prototype.handleFiles = function (files) {
    const self = this;

    files.forEach(function (file) {
      // Größen-Check
      if (file.size > CONFIG.MAX_FILE_SIZE) {
        self.showToast(
          'Die Datei "' + file.name + '" ist zu groß (max. 10 MB).',
          true
        );
        return;
      }

      // Duplikat-Check
      const isDuplicate = self.selectedFiles.some(function (f) {
        return f.name === file.name && f.size === file.size;
      });

      if (isDuplicate) {
        self.showToast('Die Datei "' + file.name + '" wurde bereits hinzugefügt.', true);
        return;
      }

      self.selectedFiles.push(file);
      self.addPreview(file);
    });
  };

  // ---------------------------------------------------------
  // PREVIEW
  // ---------------------------------------------------------

  WatchPurchaseForm.prototype.addPreview = function (file) {
    if (!this.previewGrid) return;

    const isHeic = file.name.toLowerCase().endsWith('.heic');
    const previewItem = document.createElement('div');
    previewItem.className = 'wpf__preview-item';
    previewItem.dataset.filename = file.name;
    previewItem.dataset.filesize = file.size;
    previewItem.setAttribute('role', 'listitem');

    const removeBtn = '<button type="button" class="wpf__preview-remove" aria-label="' +
      this.escapeHtml(file.name) + ' entfernen">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true">' +
      '<path d="M6 6l12 12M6 18L18 6"/>' +
      '</svg></button>';

    const filename = '<div class="wpf__preview-filename">' + this.escapeHtml(file.name) + '</div>';

    if (isHeic) {
      previewItem.innerHTML =
        '<div class="wpf__preview-heic">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
            '<rect x="3" y="3" width="18" height="18" rx="2"/>' +
            '<circle cx="8.5" cy="8.5" r="1.5"/>' +
            '<path d="M21 15l-5-5L5 21"/>' +
          '</svg>' +
          '<span>HEIC-Datei</span>' +
        '</div>' +
        filename +
        removeBtn;
      this.previewGrid.appendChild(previewItem);
    } else {
      const reader = new FileReader();
      const self = this;

      reader.onload = function (e) {
        previewItem.innerHTML =
          '<img src="' + e.target.result + '" alt="' + self.escapeHtml(file.name) + '" class="wpf__preview-image">' +
          filename +
          removeBtn;
        self.previewGrid.appendChild(previewItem);
      };

      reader.readAsDataURL(file);
    }
  };

  WatchPurchaseForm.prototype.bindPreviewRemove = function () {
    const self = this;
    if (!this.previewGrid) return;

    this.previewGrid.addEventListener('click', function (e) {
      const btn = e.target.closest('.wpf__preview-remove');
      if (!btn) return;

      const previewItem = btn.closest('.wpf__preview-item');
      if (!previewItem) return;

      const filename = previewItem.dataset.filename;
      const filesize = parseInt(previewItem.dataset.filesize, 10);

      self.selectedFiles = self.selectedFiles.filter(function (f) {
        return !(f.name === filename && f.size === filesize);
      });

      previewItem.remove();
    });
  };

  // ---------------------------------------------------------
  // SUBMIT
  // ---------------------------------------------------------

  WatchPurchaseForm.prototype.bindSubmit = function () {
    const self = this;
    this.form.addEventListener('submit', function (e) {
      e.preventDefault();
      self.handleSubmit();
    });
  };

  WatchPurchaseForm.prototype.handleSubmit = async function () {
    // Native Browser-Validation (HTML5 required-Felder)
    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      this.showToast('Bitte füllen Sie alle Pflichtfelder aus.', true);
      return;
    }

    if (this.selectedFiles.length === 0) {
      this.showToast('Bitte laden Sie mindestens ein Foto der Uhr hoch.', true);
      // Scroll zur Upload-Area
      if (this.uploadArea) {
        this.uploadArea.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    this.setLoading(true, 'Bilder werden hochgeladen …');

    try {
      // SCHRITT 1: Presigned URLs anfordern
      const urls = await this.requestPresignedUrls();

      // SCHRITT 2: Bilder parallel zu S3 hochladen
      await this.uploadFilesToS3(urls);

      // SCHRITT 3: E-Mail-Benachrichtigung senden
      this.setLoading(true, 'Anfrage wird gesendet …');
      const result = await this.submitFormData(urls);

      // Erfolg
      this.showSuccess(result);
    } catch (error) {
      console.error('[WatchPurchase] Submit-Fehler:', error);
      this.showToast(
        'Es gab ein Problem beim Senden. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt.',
        true
      );
      this.setLoading(false);
    }
  };

  // ---------------------------------------------------------
  // AWS — Presigned URLs anfordern
  // ---------------------------------------------------------

  WatchPurchaseForm.prototype.requestPresignedUrls = async function () {
    const filesMeta = this.selectedFiles.map(function (f) {
      return {
        name: f.name,
        type: f.type || 'application/octet-stream'
      };
    });

    const response = await fetch(CONFIG.API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'getPresignedUrls',
        files: filesMeta
      })
    });

    if (!response.ok) {
      throw new Error('Presigned URLs konnten nicht angefordert werden (' + response.status + ')');
    }

    const data = await response.json();
    if (!data.urls || !Array.isArray(data.urls)) {
      throw new Error('Unerwartete Antwort von der API');
    }

    return data.urls;
  };

  // ---------------------------------------------------------
  // AWS — Dateien zu S3 hochladen
  // ---------------------------------------------------------

  WatchPurchaseForm.prototype.uploadFilesToS3 = async function (urls) {
    const uploadPromises = this.selectedFiles.map(function (file) {
      const uploadInfo = urls.find(function (u) {
        return u.fileName === file.name;
      });

      if (!uploadInfo) {
        return Promise.reject(new Error('Keine Upload-URL für ' + file.name));
      }

      return fetch(uploadInfo.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream'
        }
      }).then(function (res) {
        if (!res.ok) {
          throw new Error('Upload fehlgeschlagen: ' + file.name);
        }
        return res;
      });
    });

    return Promise.all(uploadPromises);
  };

  // ---------------------------------------------------------
  // AWS — Formulardaten + fileKeys senden
  // ---------------------------------------------------------

  WatchPurchaseForm.prototype.submitFormData = async function (urls) {
    const formData = new FormData(this.form);

    const payload = {
      action: 'sendEmail',
      data: {
        locale: (document.documentElement.lang || 'de').slice(0, 2).toLowerCase(),
        personalInfo: {
          firstName: formData.get('firstName'),
          lastName: formData.get('lastName'),
          email: formData.get('email'),
          phone: formData.get('phone')
        },
        watchDetails: {
          brand: formData.get('brand'),
          model: formData.get('model'),
          referenceNumber: formData.get('referenceNumber'),
          year: formData.get('year'),
          condition: formData.get('condition')
        },
        accessories: {
          hasBox: formData.get('hasBox'),
          hasPapers: formData.get('hasPapers')
        },
        additional: {
          priceExpectation: formData.get('priceExpectation'),
          additionalInfo: formData.get('additionalInfo')
        },
        fileKeys: urls.map(function (u) { return u.fileKey; })
      }
    };

    const response = await fetch(CONFIG.API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error('E-Mail konnte nicht gesendet werden (' + response.status + ')');
    }

    // Response kann eine Ticket-ID oder ähnliches liefern
    try {
      return await response.json();
    } catch (e) {
      return {};
    }
  };

  // ---------------------------------------------------------
  // LOADING STATE
  // ---------------------------------------------------------

  WatchPurchaseForm.prototype.setLoading = function (isLoading, text) {
    if (!this.submitButton) return;

    if (isLoading) {
      this.submitButton.disabled = true;
      this.submitButton.classList.add('is-loading');
      if (this.submitLabel && text) {
        this.submitLabel.textContent = text;
      }
    } else {
      this.submitButton.disabled = false;
      this.submitButton.classList.remove('is-loading');
      if (this.submitLabel) {
        this.submitLabel.textContent = this.defaultSubmitText;
      }
    }
  };

  // ---------------------------------------------------------
  // SUCCESS OVERLAY
  // ---------------------------------------------------------

  WatchPurchaseForm.prototype.showSuccess = function (result) {
    var ticket = (result && result.ticketId) || this.generateTicketId();

    // Gemeinsames Erfolgs-Overlay öffnen (Fokus/Scroll-Lock/Schliessen via form-kit.js)
    if (window.FormKit && typeof window.FormKit.openOverlay === 'function') {
      window.FormKit.openOverlay(this.overlayId, ticket);
    }

    // Formular im Hintergrund zurücksetzen
    this.resetForm();
    this.setLoading(false);
  };

  WatchPurchaseForm.prototype.resetForm = function () {
    this.form.reset();
    this.selectedFiles = [];
    if (this.previewGrid) {
      this.previewGrid.innerHTML = '';
    }
    if (this.fileInput) {
      this.fileInput.value = '';
    }
  };

  WatchPurchaseForm.prototype.generateTicketId = function () {
    const now = new Date();
    const year = now.getFullYear();
    const pad = function (n) { return String(n).padStart(2, '0'); };
    const timestamp = pad(now.getMonth() + 1) + pad(now.getDate()) + pad(now.getHours()) + pad(now.getMinutes());
    const random = Math.floor(Math.random() * 900 + 100);
    return 'UI-' + year + '-' + timestamp + random;
  };

  // ---------------------------------------------------------
  // TOAST
  // ---------------------------------------------------------

  WatchPurchaseForm.prototype.showToast = function (message, isError) {
    if (!this.toast || !this.toastMessage) return;

    this.toastMessage.textContent = message;
    this.toast.classList.toggle('is-error', !!isError);
    this.toast.hidden = false;

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    const self = this;
    this.toastTimer = setTimeout(function () {
      self.toast.hidden = true;
    }, CONFIG.TOAST_DURATION);
  };

  // ---------------------------------------------------------
  // UTILITY
  // ---------------------------------------------------------

  WatchPurchaseForm.prototype.escapeHtml = function (str) {
    if (typeof str !== 'string') return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

})();